/** The receipt drawer: from a figure back to the document it came from.
 *
 * `CLAUDE.md` rule 3 in the interface. Two modes, because a KPI is usually a sum:
 *
 * - **breakdown** — the figures composing a total, each opening its own receipt;
 * - **receipt** — one figure's whole chain: activity, factor with publisher and edition, licence
 *   attribution, GWP set, the arithmetic, and the ledger entry that recorded it.
 *
 * It sits in the grid rather than floating over it, so the figure you clicked stays visible beside
 * its provenance. Comparing the two is the entire point; a modal would hide the thing being
 * explained.
 *
 * **Focus is the sharpest accessibility case in the product** (EXP-39, UX-03), and it is handled
 * here rather than by each screen. Opening a receipt is a navigation — the drawer's state is in the
 * URL — so React mounts a new region at the far end of the document and leaves focus on the row
 * button that was clicked. Three failures followed, and none of them is visible to a sighted
 * mouse user:
 *
 * - A keyboard reader pressed Enter on a figure and nothing happened as far as they could tell:
 *   the next Tab went to the *next row*, not into the explanation that had just appeared.
 * - Escape did nothing, so the only way out was tabbing forward through the whole receipt.
 * - Closing it left focus on a node React had removed, which browsers resolve by moving focus to
 *   `<body>` — so the next Tab started at the top of the document, ten rail destinations away
 *   from the row the reader was on. That is the dumped-at-the-top failure, and it is worse than
 *   never having opened the drawer.
 *
 * The three rules are therefore: focus moves **into** the drawer on open, Escape closes it, and
 * closing returns focus to **whatever opened it** — not to a remembered selector, because the
 * opener is a row button, a KPI, a record's figure list or the drawer's own "open the figure that
 * replaced it", and a screen cannot know which.
 */

import { Link } from "@tanstack/react-router";
import { Fragment, useEffect, useRef } from "react";

import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import type { Figure, Scope2Method } from "../api/queries";
import { useReceipt } from "../api/queries";
import { AssuranceAbout, HEADING as ASSURANCE_HEADING } from "./AssuranceAbout";
import { RaiseAboutThis } from "./RaiseAboutThis";
import { ChainLink, ReceiptChain } from "./ReceiptChain";
import { buildChain } from "./receiptLinks";
import type { NodeKey } from "./receiptLinks";
import { OutsideEngagement } from "./OutsideEngagement";
import { WindowLockBanner } from "./WindowLockBanner";
import { calculationNoteWords } from "../lib/calculationWords";
import { calculationVersionWords, gwpRefWords } from "../lib/customerWords";
import {
  computedQuantity,
  exactTonnes,
  factorValue,
  gasMass,
  instantDate,
  quantity,
  shortDate,
  shortHash,
  statedQuantity,
  tonnes,
  unitLabel,
} from "../lib/format";
import { render, RENDER_CAP } from "../screens/register";
import { cutNote, shareInWindow } from "../screens/drawerFigures";
import { sumDecimals } from "../lib/decimal";
import { isEngaged, isRefusedAsAbsent, outsideEngagementSentence } from "../lib/engagement";
import { A_COLLEAGUE, PersonName } from "../screens/PersonRef";
import { keepSearch } from "../screens/search";
import { ReadFailed } from "./Loaded";
import { isWithheld, WITHHELD_DOCUMENT } from "./withheldDocument";
import styles from "./ReceiptDrawer.module.css";

/** `Factor.unit_out` for a factor that counts carbon dioxide alone (ADR-0197). */
const CO2_ONLY = "kgCO2";

/** The mass unit a factor's number is in. Only an explicit `kgCO2e` is written as CO2e. */
function gasUnitLabel(unitOut: string | null | undefined): string {
  if (unitOut === CO2_ONLY) return "kgCO₂";
  if (unitOut === "kgCO2e") return "kgCO₂e";
  return "kg (gas basis not stated)";
}

/** A breakdown whose figures are **a page of a larger selection**, read from the server a page at a
 * time. Absent, the list is the whole selection and the drawer behaves as it always did. */
export interface BreakdownPaging {
  /** How many figures the selection holds (the server's count), of which `figures` is a part. */
  total: number;
  canLoadMore: boolean;
  /** More exists, but this drawer will not list it: said in words, with the register as the way on. */
  ceilingReached: boolean;
  onMore: () => void;
  loadingMore: boolean;
  /** A later page failed to load; what is already listed stays. */
  moreError?: unknown;
}

export type DrawerState =
  | {
      kind: "breakdown";
      label: string;
      figures: readonly Figure[];
      paging?: BreakdownPaging;
      /** The **server's** total for exactly this selection, in kilograms. When given it is the
       * number at the top and the list is never summed; when absent the top is the sum of the list,
       * which is only stated when the list is the whole selection. */
      headline?: string;
      /** A sentence the screen owes the reader about this selection, printed under the list. */
      note?: string;
    }
  /** A breakdown whose figures are read **when it is opened** — a KPI is the server's sum, so the
   * list behind it is fetched on demand — and have not arrived, or could not. Said, not blank: an
   * empty drawer is a claim that nothing composes the total. */
  | { kind: "breakdown_unread"; label: string; error: unknown; onRetry: () => void }
  | {
      kind: "receipt";
      calculationId: string;
      /** The chain node to scroll to and mark (`ReceiptChain` node keys), from an evidence-meter
       * click. Absent opens the receipt at the top, as it always did. */
      part?: string;
      backLabel?: string;
      /** The **issued filing** this receipt was opened from, by artifact id (UX-02). The receipt
       * then says which rules version that filing used instead of leaving the reader to assume
       * today's. */
      filingArtifactId?: string;
    };

interface Props {
  state: DrawerState;
  method: Scope2Method;
  onClose: () => void;
  onSelect: (calculationId: string) => void;
  onBack?: () => void;
}

export function ReceiptDrawer({ state, method, onClose, onSelect, onBack }: Props) {
  const drawer = useRef<HTMLElement>(null);
  /** Whatever had focus when this mounted — the control that opened the drawer.
   *
   * A ref rather than state, and captured **once**: the drawer stays mounted while the reader moves
   * from a breakdown to one of its receipts and back, so re-reading `activeElement` on each render
   * would end up remembering a control inside the drawer and "closing" would restore focus to a
   * node that has just been removed.
   */
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const active = document.activeElement;
    // `<body>` is not an opener. It is what `activeElement` reports when nothing is focused, which
    // is the case for a **shared receipt link** opened cold — and "restoring" focus to the body on
    // close is the dumped-at-the-top failure again, arrived at through the code that prevents it.
    opener.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
    // The region itself, not the first control in it. A reader who has just asked where a number
    // came from wants the answer read from the top; landing on the close button would announce the
    // way out of an explanation they have not heard yet.
    drawer.current?.focus();
    return () => {
      // `isConnected`, because the ordinary case for a drawer disappearing is not the close button
      // — it is a change of period or a navigation, which can remove the row that opened it too.
      // Calling `focus()` on a detached node silently does nothing and leaves focus on `<body>`,
      // which is exactly the failure this effect exists to prevent, wearing a fix's clothes.
      const previous = opener.current;
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  return (
    <aside
      ref={drawer}
      className={styles.drawer}
      // Named for what it holds rather than "Provenance", which is a word for the concept and not
      // for the thing a listener has just been moved into.
      aria-label={
        state.kind === "breakdown" || state.kind === "breakdown_unread"
          ? `${state.label} — figures`
          : "Receipt"
      }
      // Focusable only programmatically: the drawer must not become a Tab stop of its own on the
      // way down a register, because then every reader pays for a region only some of them opened.
      tabIndex={-1}
      // The hook `tokens.css` places the drawer by on a screen whose body scrolls.
      data-receipt-drawer=""
      // On the region rather than on `document`: a key listener on the document would close this
      // from inside a text box on another screen, and there is one — the register's search field
      // is two columns to the left. React's synthetic events bubble from whatever inside the
      // drawer has focus, which includes the region itself.
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.stopPropagation();
        onClose();
      }}
    >
      {state.kind === "breakdown" ? (
        <Breakdown state={state} onClose={onClose} onSelect={onSelect} method={method} />
      ) : state.kind === "breakdown_unread" ? (
        <>
          <Head title={state.label} onClose={onClose} closes="breakdown" />
          <div className={styles.scroll}>
            {state.error ? (
              <ReadFailed
                of="The figures behind this total"
                error={state.error}
                onRetry={state.onRetry}
              />
            ) : (
              <p className={styles.note} role="status">
                Loading the figures behind this total…
              </p>
            )}
          </div>
        </>
      ) : (
        <ReceiptBody
          calculationId={state.calculationId}
          filingArtifactId={state.filingArtifactId}
          part={state.part}
          backLabel={state.backLabel}
          onBack={onBack}
          onClose={onClose}
          // Opening the replacement reuses the same selection path a KPI uses, so the drawer's
          // back button works from it — a reader who follows a restatement forward can get back.
          onSelectCurrent={onSelect}
        />
      )}
    </aside>
  );
}

function Head({
  title,
  sub,
  onClose,
  closes = "receipt",
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  /** What this close button closes, for its accessible name. */
  closes?: string;
}) {
  return (
    <header className={styles.head}>
      <div>
        {/* A heading element, not a styled span. A screen reader's heading list is how somebody
            finds their way around a long page, and the drawer — the longest thing on the screen
            and the only one that appears on demand — had no entry in it. `h2` because the shell's
            `h1` is the screen's title. */}
        <h2 className={styles.headTitle}>{title}</h2>
        {sub ? <span className={styles.headSub}>{sub}</span> : null}
      </div>
      {/* **`×` is not a name.** `aria-label="Close"` was better than nothing and still announced
          one of several closes on a screen as simply "Close"; a listener moving by control has to
          know which thing goes away. */}
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label={`Close this ${closes}`}
      >
        <span aria-hidden="true">×</span>
      </button>
    </header>
  );
}

function Breakdown({
  state,
  method,
  onClose,
  onSelect,
}: {
  state: Extract<DrawerState, { kind: "breakdown" }>;
  method: Scope2Method;
  onClose: () => void;
  onSelect: (id: string) => void;
}) {
  const { paging } = state;
  const complete = !paging || state.figures.length >= paging.total;
  // **The number at the top is the server's when it has one**, and otherwise a sum of the list only
  // if the list is all of the selection. A sum of the part that happened to be loaded is a total of
  // nothing in particular, and this drawer never prints one.
  // Exact, and over each figure's share of the window (G-P7-05): the rows of this list are what the
  // total counts, so a straddling bill is its days inside the period and never the whole record.
  const listed = sumDecimals(state.figures.map(shareInWindow));
  const headline = state.headline ?? (complete ? listed : undefined);
  // The same cap and the same reason as the register's table (EXP-39): a breakdown of four
  // thousand figures is four thousand buttons. A paged drawer is bounded by its own ceiling and
  // lists what it has loaded; the cap would hide the second page the reader asked for.
  const { shown, hidden } = render(
    state.figures,
    paging ? Math.max(RENDER_CAP, state.figures.length) : RENDER_CAP,
  );
  const count = quantity(String(paging ? paging.total : state.figures.length));

  return (
    <>
      <Head
        title={state.label}
        sub={
          paging && !complete
            ? `Showing ${quantity(String(state.figures.length))} of ${count} figures · every one opens its receipt`
            : `${count} ${count === "1" ? "figure" : "figures"} · every one opens its receipt`
        }
        onClose={onClose}
        closes="breakdown"
      />
      {headline !== undefined ? (
        <div className={styles.figure} data-testid="breakdown-headline">
          {tonnes(headline)}
          <span className={styles.figureUnit}>tCO₂e</span>
        </div>
      ) : null}
      <div className={styles.scroll}>
        <div className={styles.list}>
          {shown.map((figure) => (
            <button
              key={figure.id}
              type="button"
              className={styles.listRow}
              onClick={() => onSelect(figure.id)}
              // Every row's label is "a site name, a scope and a tonnage" — three spans a sighted
              // reader takes in at once and a listener hears as one run-on string with no idea
              // which part is the figure. Named explicitly, with the unit, because "408.6" is not
              // a quantity.
              aria-label={`Receipt for ${figure.facility_name}, scope ${figure.scope} ${figure.category.replace(/_/g, " ")}, ${tonnes(shareInWindow(figure))} tonnes CO2 equivalent${cutNote(figure) ? `, ${cutNote(figure) ?? ""}` : ""}`}
            >
              <span className={styles.listName}>
                {figure.facility_name}
                <span className={styles.listSub}>
                  Scope {figure.scope} · {figure.category.replace(/_/g, " ")}
                  {figure.method ? ` · ${figure.method === "market" ? "market" : "location"}` : ""}
                  {figure.market_tier ? ` · tier ${figure.market_tier.replace(/_/g, " ")}` : ""}
                  {cutNote(figure) ? <span data-testid="breakdown-cut"> · {cutNote(figure)}</span> : null}
                </span>
              </span>
              <span className={`${styles.listValue} num`}>{tonnes(shareInWindow(figure))}</span>
              <span className={styles.chev} aria-hidden="true">
                ›
              </span>
            </button>
          ))}
        </div>
        {hidden > 0 ? (
          <p className={styles.note}>
            Listing the largest {shown.length} of {state.figures.length}. The total above is over
            every one of them; the {hidden} not listed are the smallest. Narrow the register to
            reach them.
          </p>
        ) : null}
        {paging && !complete ? (
          <div className={styles.more} data-testid="breakdown-more">
            <p className={styles.note} data-testid="breakdown-showing">
              Showing {quantity(String(state.figures.length))} of {count}, largest first.
              {state.headline !== undefined
                ? " The total above is over all of them, not just those listed."
                : " No total is stated for a selection this size; the figures listed are not added up."}
            </p>
            {paging.canLoadMore ? (
              <button
                type="button"
                className={styles.moreButton}
                onClick={paging.onMore}
                disabled={paging.loadingMore}
                data-testid="breakdown-load-more"
              >
                {paging.loadingMore ? "Loading…" : "Show more"}
              </button>
            ) : null}
            {paging.ceilingReached ? (
              <p className={styles.note} data-testid="breakdown-ceiling">
                This list stops at {quantity(String(state.figures.length))} figures. The other{" "}
                {quantity(String(paging.total - state.figures.length))} are in the register, which
                can be filtered to reach them.
              </p>
            ) : null}
            {paging.moreError ? (
              <ReadFailed of="The next figures" error={paging.moreError} onRetry={paging.onMore} />
            ) : null}
          </div>
        ) : null}
        {state.note ? (
          <p className={styles.note} data-testid="breakdown-note">
            {state.note}
          </p>
        ) : null}
        {method === "market" ? (
          <p className={styles.note}>
            Market-based figures. Where an instrument does not cover the load, or fails a Scope 2
            quality criterion, the load falls through the hierarchy — each receipt says which tier
            it used.
          </p>
        ) : null}
      </div>
    </>
  );
}

function ReceiptBody({
  calculationId,
  filingArtifactId,
  part,
  backLabel,
  onBack,
  onClose,
  onSelectCurrent,
}: {
  calculationId: string;
  filingArtifactId?: string;
  part?: string;
  backLabel?: string;
  onBack?: () => void;
  onClose: () => void;
  onSelectCurrent?: (calculationId: string) => void;
}) {
  const reader = useReader();
  const { data: receipt, isPending, error } = useReceipt(calculationId, filingArtifactId);
  const { data: me } = useMe();
  // **A refusal is not a fault, for somebody whose reads a clamp narrows** (Q-03, G-Wauditor-04). An
  // engaged Auditor who opens a link to a figure outside their engagement is told by the server what
  // it tells for a figure that does not exist - the same 404, byte for byte - and the drawer used to
  // draw that as *rule 3 failing, please report it*. It says the sentence that is true of both cases.
  const outside = isEngaged(me) && isRefusedAsAbsent(error);

  /** What the drawer is doing, for somebody who cannot see it change (EXP-39).
   *
   * A receipt is a request, so opening one puts "Resolving provenance…" on screen and then replaces
   * it with the whole chain. A sighted reader watches that happen. For a listener the region they
   * were just moved into fell silent and then quietly became a different thing, with no event to
   * announce — and the one state that matters most, *this figure could not produce a receipt*, is
   * the one they would never hear at all.
   *
   * It is rendered in all three branches below rather than once around them, because an
   * `aria-live` region has to be in the document **before** its text changes: inserted at the
   * moment it has something to say, it is frequently not announced. Three returns, one region, same
   * node — React keeps it across the re-render because it is in the same position in each.
   */
  const announcement = isPending
    ? "Resolving this figure's provenance."
    : outside
      ? outsideEngagementSentence("figure")
      : error || !receipt
      ? "This figure could not produce a receipt."
      : `Receipt for scope ${receipt.scope} ${receipt.category.replace(/_/g, " ")} at ${receipt.facility_name}: ${tonnes(receipt.figure_kgco2e, 2)} tonnes ${
          receipt.factor.unit_out === CO2_ONLY ? "CO2, excluding methane and nitrous oxide" : "CO2 equivalent"
        }, ${
          receipt.is_complete ? "fully traceable" : "evidence chain incomplete"
        }${receipt.is_current ? "" : ", and restated — no longer the figure in force"}.`;

  const live = (
    <p className="visuallyHidden" role="status" aria-live="polite" data-testid="receipt-live">
      {announcement}
    </p>
  );

  if (isPending) {
    return (
      <>
        <Head title="Receipt" onClose={onClose} />
        {live}
        <p className={styles.loading}>Resolving provenance…</p>
      </>
    );
  }

  if (outside) {
    return (
      <>
        <Head title="Receipt" onClose={onClose} />
        {live}
        <OutsideEngagement noun="figure" testId="receipt-outside-engagement" />
      </>
    );
  }

  if (error || !receipt) {
    return (
      <>
        <Head title="Receipt" onClose={onClose} />
        {live}
        {/* `role="alert"` as well as the status region: a receipt that cannot be produced is rule 3
            failing, and it is the one state in this drawer worth interrupting a reader for. */}
        <p className={styles.error} role="alert">
          This figure could not produce a receipt. It should not have been shown — rule 3. Please
          report it.
        </p>
      </>
    );
  }

  /** The arithmetic, stated so it is actually true.
   *
   * A market-based figure prices only the **uncovered** load: Trafford Park consumed 3,120,000 kWh
   * but a REGO covered 2,600,000 of it, so the figure is 520,000 × 0.13096. Writing
   * "3,120,000 × 0.13096 = 68,099.2" — as this did — is arithmetic that does not hold, in the one
   * place whose entire job is showing how a number was reached.
   */
  const covered = receipt.components["covered_kWh"];
  const uncovered = receipt.components["uncovered_kWh"];
  const isPartlyCovered = covered !== undefined && Number(covered) > 0;

  // What the factor's number counts. A factor that is carbon dioxide alone must not be written as
  // CO2e on the screen whose job is showing how a number was reached; `null` (the factor row could
  // not be found) is not CO2e either, so only an explicit `kgCO2e` earns the "e".
  const co2Only = receipt.factor.unit_out === CO2_ONLY;
  const gasUnit = gasUnitLabel(receipt.factor.unit_out);
  // **A refrigerant has no emission-factor row: its multiplier is the gas's GWP** (a mass balance,
  // not a published factor), so it has no publisher, no edition, no identifier and no unit. The
  // card used to print ". 1924 kgCO2e per" over three dashes while the chain said "priced: Met"
  // (US walkthrough, C5). It now says what the number is and where it comes from - the GWP set
  // under Method - instead of showing empty provenance rows that read as a gap.
  const isGwp =
    receipt.factor.value !== null &&
    !receipt.factor.publisher &&
    !receipt.factor.unit_in &&
    receipt.category.includes("fugitive");

  const arithmetic: string[] = [];
  /** The hover text of a rounded line: the exact value it was rounded from. */
  const exactOf = new Map<string, string>();
  if (receipt.factor.value === null) {
    arithmetic.push(`${quantity(receipt.figure_kgco2e, 1)} kgCO₂e`);
  } else if (isPartlyCovered && uncovered !== undefined) {
    arithmetic.push(`${statedQuantity(receipt.activity_quantity)} ${unitLabel(receipt.activity_unit)} consumed`);
    arithmetic.push(`− ${statedQuantity(covered)} kWh covered by an instrument, at 0 kgCO₂e/kWh`);
    arithmetic.push(
      `= ${statedQuantity(uncovered)} kWh × ${factorValue(receipt.factor.value)} = ${quantity(receipt.figure_kgco2e, 1)} ${gasUnit}`,
    );
  } else {
    /** **The operands are the ones the factor was multiplied by** (walkthrough C4, rules 3 and 5).
     *
     * The engine converts what was entered into the unit the factor is published per before it
     * prices, so `8,000 therms × 53.1145` was arithmetic that does not hold: the factor is per MMBtu
     * and the product it printed was a tenth of the true one's operand. The conversion is a step of
     * its own, stated on its own line, and the multiplication uses the converted quantity. A
     * receipt from before the server stated it (`priced_quantity` absent) falls back to what was
     * entered, which is right whenever no conversion happened.
     *
     * Quantities are printed with every digit they were given (`statedQuantity`): `3.5 kg` read as
     * `4 kg` made `4 × 1924` the arithmetic of a different figure.
     */
    const priced = receipt.priced_quantity ?? receipt.activity_quantity;
    const pricedUnit = receipt.priced_unit ?? receipt.activity_unit;
    const converted =
      pricedUnit !== receipt.activity_unit || Number(priced) !== Number(receipt.activity_quantity);
    // A quantity the engine converted is a long decimal by construction (667,280 kWh is
    // 2276.853870291598553211001004 MMBtu): shown to three places, with every digit as hover text.
    // One the person gave stays exactly as they gave it.
    const pricedShown = converted ? computedQuantity(priced) : statedQuantity(priced);
    const exactNote = converted ? `Exactly ${factorValue(priced)} ${unitLabel(pricedUnit)}` : undefined;
    if (converted) {
      const line = `${statedQuantity(receipt.activity_quantity)} ${unitLabel(receipt.activity_unit)} = ${pricedShown} ${unitLabel(pricedUnit)} (converted to the unit the factor is published per)`;
      arithmetic.push(line);
      if (exactNote) exactOf.set(line, exactNote);
    }
    {
      const line = `${pricedShown} ${unitLabel(pricedUnit)} × ${factorValue(receipt.factor.value)}${converted ? ` ${gasUnit} per ${unitLabel(pricedUnit)}` : ""} = ${quantity(receipt.figure_kgco2e, 1)} ${gasUnit}${co2Only ? " (CO₂ only)" : ""}`;
      arithmetic.push(line);
      if (exactNote) exactOf.set(line, exactNote);
    }
  }

  /** What each node prints. The frame, the standing and the break are `ChainLink`'s. */
  const nodeBody = (key: NodeKey): React.ReactNode => {
    switch (key) {
      case "source":
        // **Two legitimate shapes, and the receipt names which one** (ADR-0036 decision 2). A figure
        // recorded on a person's attestation has no document, and rendering it with an empty
        // Document row would read as a missing one. So the attestation is shown as the evidence it
        // is, and `stored` and `retrievable` are reported as not applicable rather than broken. A
        // manual figure must never render as a documented one. A supplier's copied figure takes
        // this shape too: its basis is the supplier's, and it is graded C.
        return receipt.reference_kind === "stated_basis" ? (
          <>
            <div className={styles.kv} data-testid="stated-basis">
              <span className={styles.k}>Basis</span>
              <span className={styles.v}>{receipt.stated_basis}</span>
              <span className={styles.k}>Attested</span>
              <span className={styles.v}>
                {instantDate(receipt.basis_attested_at ?? null, reader)}
                {/* The grade, beside the basis rather than only in the register: a reader looking at
                    what a figure rests on needs to know this one is not a metered reading traced to
                    a bill we hold. */}
                <span className={styles.missing}> · no document</span>
              </span>
            </div>
            {receipt.supplier_source ? (
              // **ADR-0225.** A supplier's figure reported as Scope 3 names the three rows that make
              // it lawful and checkable - the request it answers, the answer it was copied from and
              // the agreement it was copied under - as the short identifiers the rest of the chain
              // uses, never a supplier's name. And it says whether the category was the buyer's
              // statement or the default, because "the buyer said 1" and "nobody said, so 1" are
              // different facts years later.
              <div className={styles.kv} data-testid="supplier-source">
                <span className={styles.k}>Request</span>
                <span className={`${styles.v} ${styles.mono}`}>
                  {receipt.supplier_source.request_id.slice(0, 8)}
                </span>
                <span className={styles.k}>Answer</span>
                <span className={`${styles.v} ${styles.mono}`}>
                  {receipt.supplier_source.submission_id.slice(0, 8)}
                </span>
                {receipt.supplier_source.agreement_id ? (
                  <>
                    <span className={styles.k}>Agreement</span>
                    <span className={`${styles.v} ${styles.mono}`}>
                      {receipt.supplier_source.agreement_id.slice(0, 8)}
                    </span>
                  </>
                ) : null}
                {receipt.supplier_source.scope3_category != null ? (
                  <>
                    <span className={styles.k}>Reported as</span>
                    <span className={styles.v}>
                      Scope 3, category {receipt.supplier_source.scope3_category}
                      {receipt.supplier_source.category_stated ? "" : " (the default)"}
                    </span>
                  </>
                ) : null}
                <span className={styles.k}>Method</span>
                <span className={styles.v}>{receipt.supplier_source.method}</span>
              </div>
            ) : null}
          </>
        ) : (
          <div className={styles.kv}>
            <span className={styles.k}>Document</span>
            <span className={styles.v}>
              {isWithheld(receipt.source_document) ? (
                // D-06: the file also covers sites outside the engagement. No name, no link (its
                // own page answers this reader as an unknown id), and the hash on the next row.
                <span className={styles.missing} data-testid="document-withheld">
                  {WITHHELD_DOCUMENT}
                </span>
              ) : receipt.source_document ? (
                // The document's own record page (state, SHA-256, the file, the figures from it),
                // not the live Review form: a receipt is evidence and a form invites an edit (Q-24).
                <Link
                  to="/records/$recordId"
                  params={{ recordId: receipt.source_document.id }}
                  search={keepSearch}
                  className={styles.link}
                >
                  {receipt.source_document.original_filename ?? "Untitled document"}
                </Link>
              ) : receipt.source_document_id ? (
                // An id that resolves to nothing: said plainly, rather than printing a UUID and
                // letting a reader assume it would reach something.
                <span className={styles.missing}>a document this figure cites, which could not be found</span>
              ) : (
                "— none"
              )}
            </span>
            {isWithheld(receipt.source_document) && receipt.source_document?.sha256 ? (
              <>
                <span className={styles.k}>SHA-256</span>
                <span className={styles.v} data-testid="document-withheld-hash">
                  {receipt.source_document.sha256}
                </span>
              </>
            ) : null}
            {receipt.source_document ? (
              <>
                <span className={styles.k}>State</span>
                <span className={styles.v}>
                  {receipt.source_document.state.replace(/_/g, " ")}
                  {receipt.source_document.retention_expired ? (
                    <span className={styles.missing}> · retention expired</span>
                  ) : null}
                </span>
              </>
            ) : null}
          </div>
        );

      case "activity":
        return (
          <>
            <div className={styles.stepBody}>
              {statedQuantity(receipt.activity_quantity)} {unitLabel(receipt.activity_unit)}
            </div>
            <div className={styles.kv}>
              <span className={styles.k}>Period</span>
              <span className={styles.v}>
                {shortDate(receipt.activity_period_start)} – {shortDate(receipt.activity_period_end)}
              </span>
              <span className={styles.k}>Record</span>
              {/* **A link, not an identifier.** A bare UUID ends the chain one link early: it names
                  the thing and gives the reader no way to reach it. A router link rather than an
                  `href`, with `keepSearch` leaving the drawer's own parameters behind - a receipt id
                  carried onto the record page would reopen this drawer over it. */}
              <span className={styles.v}>
                <Link
                  to="/records/$recordId"
                  params={{ recordId: receipt.activity_record_id }}
                  search={keepSearch}
                  className={styles.link}
                >
                  Open the record
                </Link>
              </span>
            </div>
          </>
        );

      case "factor":
        // No factor value: the node already says no emission factor was applied, in words. A
        // publisher and an edition are not invented for a figure that was not priced from one.
        if (receipt.factor.value === null) return null;
        return (
          <>
            {isGwp ? (
              <div className={styles.stepBody} data-testid="factor-is-gwp">
                Global warming potential{" "}
                <span className="num">{factorValue(receipt.factor.value)}</span> {gasUnit} per kg of
                refrigerant released
                {receipt.factor.gwp_set_label ? `, from the ${receipt.factor.gwp_set_label} set` : ""}
                {receipt.factor.gwp_refs.length > 0 ? ` (${gwpRefWords(receipt.factor.gwp_refs)})` : ""}
              </div>
            ) : (
              <div className={styles.stepBody}>
                {receipt.factor.publisher ?? "—"}
                {" · "}
                <span className="num">{factorValue(receipt.factor.value)}</span> {gasUnit} per{" "}
                {unitLabel(receipt.factor.unit_in ?? "")}
              </div>
            )}
            {co2Only ? (
              <p className={styles.note} data-testid="co2-only">
                <strong>CO₂ only.</strong> This factor excludes CH₄ and N₂O, so the figure is lower than a
                full CO₂e figure.
              </p>
            ) : null}
            <div className={styles.kv}>
              {isGwp ? null : (
                <>
                  <span className={styles.k}>Edition</span>
                  <span className={styles.v}>{receipt.factor.edition ?? "—"}</span>
                  <span className={styles.k}>Identifier</span>
                  <span className={`${styles.v} ${styles.mono}`}>
                    {receipt.factor.external_id ?? "—"}
                  </span>
                  <span className={styles.k}>Retrieved</span>
                  <span className={styles.v}>{shortDate(receipt.factor.retrieved_at)}</span>
                </>
              )}
              {receipt.factor.source_url ? (
                <>
                  {/* Where the number was read (rule 3): the file, the publisher's date for it and the
                      hash of the bytes parsed, so a reader can fetch it and check. */}
                  <span className={styles.k}>Source</span>
                  <a
                    className={`${styles.v} ${styles.mono} ${styles.wrap}`}
                    href={receipt.factor.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="factor-source-url"
                  >
                    {receipt.factor.source_url}
                  </a>
                  <span className={styles.k}>Published</span>
                  <span className={styles.v}>{shortDate(receipt.factor.source_published_at ?? null)}</span>
                  <span className={styles.k}>File SHA-256</span>
                  <span className={`${styles.v} ${styles.mono} ${styles.wrap}`} data-testid="factor-source-sha256">
                    {receipt.factor.source_sha256}
                  </span>
                </>
              ) : null}
            </div>
            {/* The licence condition travels with the figure. OGL v3.0 requires it wherever DESNZ
                values are shown or exported, and an evidence pack is exactly where it gets dropped
                (verification log U-09). */}
            {receipt.factor.attribution_statement ? (
              <p className={styles.licence}>{receipt.factor.attribution_statement}</p>
            ) : null}
          </>
        );

      case "method":
        return (
          <div className={styles.kv}>
            {receipt.method ? (
              <>
                <span className={styles.k}>Method</span>
                <span className={styles.v}>{receipt.method}-based</span>
              </>
            ) : null}
            <span className={styles.k}>GWP set</span>
            <span className={styles.v}>
              {receipt.factor.gwp_set_label ?? "—"}
              {receipt.factor.gwp_refs.length > 0 ? ` (${gwpRefWords(receipt.factor.gwp_refs)})` : ""}
            </span>
          </div>
        );

      case "coverage":
        // **What covered it** (ACC-07). For an assurance provider the certificate is the question:
        // whether it was retired, and whether anybody else claimed the same volume.
        return (
          <>
            {receipt.covered_by.map((c) => (
              <div key={c.instrument_id} className={styles.kv}>
                <span className={styles.k}>Instrument</span>
                <span className={styles.v}>
                  {c.name ?? (
                    /* The draw is still shown. The figure was priced against it, and omitting the
                       claim would hide exactly what somebody is checking for. */
                    <span className={styles.missing}>
                      an instrument this figure cites, which could not be found
                    </span>
                  )}
                  {c.kind ? ` · ${c.kind.replace(/_/g, " ")}` : ""}
                </span>
                <span className={styles.k}>Applied</span>
                <span className={`${styles.v} num`}>
                  {quantity(c.kwh, 0)} kWh at {factorValue(c.factor)} kgCO₂e/kWh
                </span>
                <span className={styles.k}>Vintage</span>
                <span className={styles.v}>{c.vintage_year || "—"}</span>
                <span className={styles.k}>Retired</span>
                <span className={styles.v}>
                  {shortDate(c.retired_at)}
                  {c.registry_name ? ` · ${c.registry_name}` : ""}
                  {c.certificate_reference ? ` · ${c.certificate_reference}` : ""}
                </span>
              </div>
            ))}
          </>
        );

      case "calculation":
        return (
          <>
            <div className={styles.arith}>
              {arithmetic.map((line) => (
                <div key={line} title={exactOf.get(line)}>
                  {line}
                </div>
              ))}
            </div>
            {/* Per-gas components only. Coverage is already stated in the arithmetic above, so
                repeating it here as raw keys would be noise. */}
            {gasComponents.length > 0 ? (
              <div className={styles.kv}>
                {gasComponents.map(([gas, value]) => (
                  <Fragment key={gas}>
                    <span className={styles.k}>{gas.replace(/_/g, " ")}</span>
                    {/* A gas line is a mass with its unit, three places (the display rule): it
                        printed `371.40787548` with no unit. A refrigerant's parts are kgCO₂e. */}
                    <span className={`${styles.v} num`}>{gasMass(value, isGwp ? "kgCO₂e" : gasUnit)}</span>
                  </Fragment>
                ))}
              </div>
            ) : null}
            <div className={styles.kv}>
              <span className={styles.k}>Calculation version</span>
              <span className={styles.v}>{calculationVersionWords(receipt.engine_version)}</span>
            </div>
          </>
        );

      case "verifier":
        return (
          <div className={styles.kv}>
            <span className={styles.k}>Verification</span>
            <span className={`${styles.v} ${styles.mono}`}>
              {receipt.verification_id ? (
                // What the server says this reader may be told (ADR-0218): a name for the company's
                // own roles, the role for an Auditor; the identifier on hover. The ledger still
                // holds the identifier only, and the id is never the visible text.
                <PersonName userId={receipt.verification_id} fallback={A_COLLEAGUE} />
              ) : receipt.reference_kind === "stated_basis" ? (
                "— not yet accepted"
              ) : (
                "— none"
              )}
            </span>
          </div>
        );

      case "ledger":
        return receipt.ledger.map((entry) => (
          <div key={entry.entry_id} className={styles.kv}>
            <span className={styles.k}>Entry</span>
            <span className={styles.v}>
              #{entry.seq} · {entry.type.replace(/_/g, " ")}
            </span>
            <span className={styles.k}>Hash</span>
            <span className={`${styles.v} ${styles.mono}`}>{shortHash(entry.hash)}</span>
            <span className={styles.k}>Previous</span>
            <span className={`${styles.v} ${styles.mono}`}>{shortHash(entry.prev_hash)}</span>
          </div>
        ));
    }
  };

  const nodes = buildChain(receipt);
  const gasComponents = Object.entries(receipt.components).filter(([key]) => !key.endsWith("_kWh"));

  return (
    <>
      <Head
        title={`Scope ${receipt.scope} · ${receipt.category.replace(/_/g, " ")}`}
        sub={`${receipt.facility_name}${receipt.method ? ` · ${receipt.method}-based` : ""}`}
        onClose={onClose}
      />
      {live}
      <div className={styles.figure} title={`${exactTonnes(receipt.figure_kgco2e)} tCO₂e, exact`}>
        {tonnes(receipt.figure_kgco2e, 2)}
        <span className={styles.figureUnit}>{co2Only ? "tCO₂ (CO₂ only)" : "tCO₂e"}</span>
        <span className={`${styles.badge} ${receipt.is_complete ? styles.badgeOk : styles.badgeWarn}`}>
          {receipt.is_complete ? "Traceable" : "Incomplete"}
        </span>
        {receipt.is_current ? null : <span className={`${styles.badge} ${styles.badgeWarn}`}>Restated</span>}
      </div>

      {/* Whether the window this figure was calculated for is closed (ADR-0094 D2): a receipt
          says what the figure was *under*. Nothing is rendered for an open window. */}
      <WindowLockBanner lock={receipt.window_lock} />

      {/* **The rules this receipt was reached under, when it was reached from an issued filing**
          (UX-02: opening a receipt from a report cannot silently use current-year rules). The
          version is the filing's own, read back out of the issued record - never today's. */}
      {receipt.filing ? (
        <p className={styles.note} data-testid="receipt-filing">
          <strong>Opened from an issued filing.</strong> {receipt.filing.regime_name}, rules version{" "}
          <span data-testid="receipt-filing-version">{receipt.filing.regime_version}</span>, issued{" "}
          {instantDate(receipt.filing.issued_at, reader)}: the rules that filing used, which may
          differ from the current ones.
          {/* Whether the regime was law when it was issued, in the filing's own words (REP-01):
              an enjoined regime's filing must not read as an operative one. */}
          {receipt.filing.in_force_caveat ? (
            <span data-testid="receipt-filing-caveat"> {receipt.filing.in_force_caveat}</span>
          ) : null}
          {receipt.filing.figure_in_filing ? null : (
            <span data-testid="receipt-filing-outside">
              {" "}
              This figure is not part of that filing.
            </span>
          )}
        </p>
      ) : null}

      {/* Why it is incomplete, where it says it is (F10). "Incomplete" used to be a badge with
          nothing behind it, because completeness was a non-empty column — a reader who saw it had
          no way to find out which link was missing, and no way to fix it. */}
      {receipt.is_complete ? null : (
        <p className={styles.incomplete}>
          <strong>Not fully traceable.</strong> {receipt.evidence_why_not}
          {receipt.evidence_met.length > 0 ? (
            <span className={styles.incompleteMet}>
              {" "}
              What does hold: {receipt.evidence_met.map((s) => s.replace(/_/g, " ")).join(", ")}.
              {/* A step that cannot apply is not a step that failed (ADR-0036). Listing it among
                  the missing ones would tell somebody to go and store a document for a figure that
                  never had one. */}
              {receipt.evidence_not_applicable.length > 0
                ? ` Not applicable to this figure: ${receipt.evidence_not_applicable
                    .map((s) => s.replace(/_/g, " "))
                    .join(", ")}.`
                : ""}
            </span>
          ) : null}
        </p>
      )}

      <div className={styles.scroll}>
        <div className={styles.chain}>
          {/* A superseded receipt resolves forever, and has to say so (ADR-0030 decision 4).
              Resolving it silently would be worse than failing: a figure that looks live when it has
              been replaced is one nobody goes back to check. The replacement is a link rather than
              an id, because "go and find the current number" is work the reader should not do. */}
          {receipt.is_current ? null : (
            <p className={styles.restated}>
              This figure was restated on {instantDate(receipt.superseded_at ?? null, reader)}. It is what was
              calculated at the time, and it is no longer the figure in force — do not quote it as
              current.
              {receipt.superseded_by_calculation_id ? (
                <button
                  type="button"
                  className={styles.restatedLink}
                  onClick={() => onSelectCurrent?.(receipt.superseded_by_calculation_id as string)}
                >
                  Open the figure that replaced it ›
                </button>
              ) : (
                <span className={`${styles.restatedLink} ${styles.restatedPlain}`}>
                  Nothing replaced it: the underlying record was itself corrected, so this figure is
                  no longer produced at all.
                </span>
              )}
            </p>
          )}
          {backLabel && onBack ? (
            <button type="button" className={styles.back} onClick={onBack}>
              ‹ {backLabel}
            </button>
          ) : null}

          {/* **The receipt is a chain, drawn as one** (docs/design/36 section 5). Each node's
              standing - present, not applicable, missing - is decided in `receiptLinks.ts` from the
              server's evidence assessment, and a missing link is drawn as a break with its reason
              rather than left out. The arithmetic appears once, in the calculation node. */}
          <ReceiptChain nodes={nodes}>
            {nodes.map((node) => (
              <ChainLink key={node.key} node={node} receipt={receipt} focused={node.key === part}>
                {nodeBody(node.key)}
              </ChainLink>
            ))}
          </ReceiptChain>

          {/* Questions an assurance provider has raised about the record this figure came from.
              Renders nothing unless there are some, or the read failed. These are not a link in
              the chain - they are what has been asked about it - so they sit beneath it. */}
          <AssuranceAbout
            recordId={receipt.activity_record_id}
            frame={(children) => (
              <section className={styles.aside}>
                <h3 className={styles.asideLabel}>{ASSURANCE_HEADING}</h3>
                {children}
              </section>
            )}
          />

          {/* The way into the assurance queue from the record this figure came from (gate 27). */}
          <RaiseAboutThis recordId={receipt.activity_record_id} />

          {receipt.notes.map((note) => (
            <p key={note} className={styles.note}>
              {calculationNoteWords(note)}
            </p>
          ))}
        </div>
      </div>
    </>
  );
}
